function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// -------------------------------------
// Dependencies
// -------------------------------------
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import translate from 'scripts/helpers/iamota-translate';
import Debug from 'scripts/libs/debug';
import { componentToName } from 'scripts/helpers/utilities';
import { Loadable, Disableable } from 'scripts/classes/iamota-component-helpers';
import debounce from 'lodash-es/debounce';

// -------------------------------------
// Abstract Base Class: iamForm
// -------------------------------------
class iamForm extends HTMLElement {
  /**
   * Constructor
   */
  constructor() {
    super();

    // Variables
    this._form = null;
    this.validator = null;
    this.loadable = new Loadable(this);
    this.disableable = new Disableable(this);
  }

  /**
   * Triggered when the element is connected to the DOM
   */
  connectedCallback() {
    this.errorContainer = this.querySelector(iamForm.ERROR_CONTAINER_CLASS);

    // Validate Required Attributes
    if (!this.validateRequiredAttributes(['errorContainer'])) return;
    iamotaEventHandler.subscribe('formValidation:init', this.onFormValidationInit.bind(this), {
      source: "".concat("iamForm.js", "!").concat(this.namespace)
    });
    iamotaEventHandler.subscribe("".concat(this.namespace, ":changed"), this.validateForm.bind(this), {
      source: "".concat("iamForm.js", "!").concat(this.namespace)
    });
    this.bindFormControlEventListeners();

    //iamotaEventHandler.trigger(`${this.namespace}:connected`, { iamForm: this }, { context: this, isAtomic: true });
  }

  /**
   * Returns the associated <form> element
   * Assumes this form element is either nested within a form or has a `data-form-id` attribute pointing to it's associated form
   * @returns {Object}
   * @abstract
   */
  get form() {
    // Cache it for future use (e.g. after detaching from the DOM during disconnectedCallback)
    if (!this._form) {
      this._form = this.querySelector('form');
      if (!this._form) console.error("Unable to find a <form> associated with <".concat(this.tagName.toLowerCase(), ">"));
    }
    return this._form;
  }

  /**
   * Returns the name prefix for events (must be implemented by child class)
   * @returns {string}
   */
  get formType() {
    return componentToName(this.constructor.name);
  }

  /**
   * Returns the element's namespace for event handling
   * e.g. productForm
   * @returns {string}
   * @abstract
   */
  get namespace() {
    return this.formType;
  }

  /**
   * When anything in the form changes, fire events:
   * - ${formType}:changed
   * - ${formType}:${controlName}:changed
   */
  bindFormControlEventListeners() {
    var controls = this.querySelectorAll('input, select, textarea');
    controls.forEach(control => this.addControlEventListener(control));

    // Observe for new controls being added to the form
    var observer = new MutationObserver(mutationsList => {
      for (var mutation of mutationsList) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.matches && node.matches('input, select, textarea')) {
              Debug.extreme('[iamForm] New control added:', node);
              this.addControlEventListener(node);
            } else if (node.querySelectorAll) {
              // Check for controls within the added node's subtree
              var nestedControls = node.querySelectorAll('input, select, textarea');
              nestedControls.forEach(control => {
                Debug.extreme('[iamForm] New nested control added:', control);
                this.addControlEventListener(control);
              });
            }
          });
        }
      }
    });
    observer.observe(this, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Adds event listener to a control
   * @param {HTMLElement} control
   */
  addControlEventListener(control) {
    if (control.value == null) return; // Ignore controls without a value

    // Determine the "change" event type to listen for based on the form control type
    var listenerType = 'change'; // Default to 'change' for most controls
    if (control.tagName.toLowerCase() === 'input' && !['checkbox', 'radio', 'file'].includes(control.type)) {
      listenerType = 'input'; // Use 'input' event for most <input> elements
    }
    var lastDispatchedValue = control.value;
    var triggerNow = event => {
      lastDispatchedValue = control.value;
      iamotaEventHandler.trigger(["".concat(this.namespace, ":changed"), "".concat(this.namespace, ":").concat(control.name, ":changed")], {
        iamForm: this,
        event,
        control,
        value: control.value
      }, {
        context: control
      });
    };
    var triggerDebounced = debounce(triggerNow, 500);
    control.addEventListener(listenerType, triggerDebounced);
    control.addEventListener('blur', event => {
      if (control.value !== lastDispatchedValue) {
        var _triggerDebounced$can;
        (_triggerDebounced$can = triggerDebounced.cancel) === null || _triggerDebounced$can === void 0 || _triggerDebounced$can.call(triggerDebounced); // Cancel any pending debounced calls
        triggerNow(event); // Trigger immediately on blur
      }
    });
  }

  /**
   * Triggered when the element is disconnected from the DOM
   */
  disconnectedCallback() {
    //iamotaEventHandler.trigger(`${this.namespace}:disconnected`, { iamForm: this }, { context: this, isAtomic: true });
  }

  /**
   * Validate the required attributes for the form
   */
  validateRequiredAttributes() {
    var attributeList = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    if (!Array.isArray(attributeList)) {
      console.error('Unable to validate required attributes: Invalid attribute list');
      this.setDisabled(true);
      return false;
    }
    var isValid = true;
    attributeList.forEach(attribute => {
      if (!this[attribute]) {
        console.error("Unable to initialize the ".concat(this.namespace, ": Missing required \"").concat(attribute, "\" attribute"));
        this.setDisabled(true);
        this.setErrorState(translate('An error occurred, please try refreshing the page or contact support', "".concat(this.namespace, ".error_missing_attribute")));
        isValid = false;
      }
    });
    return isValid;
  }

  /**
   * Triggered when the form validation is initialized
   */
  onFormValidationInit(_ref) {
    var validator = _ref.validator,
      form = _ref.form;
    // Confirm the form the validation is being initialized for is "this form"
    if (form !== this.form) {
      Debug.extreme("".concat(this.namespace, ": Validation initialized for a different form"), {
        validatorForm: form,
        thisForm: this.form,
        iamForm: this
      });
      return;
    }

    // Save a reference to the validator
    this.validator = validator;

    // Subscribe to Validation Events
    this.validator.onValidate(_ref2 => {
      var isValid = _ref2.isValid,
        isSubmitted = _ref2.isSubmitted,
        fields = _ref2.fields,
        groups = _ref2.groups;
      iamotaEventHandler.trigger(["".concat(this.namespace, ":validation:changed"), 'iamForm:validation:changed'], {
        isValid,
        isSubmitted,
        fields,
        groups,
        validator,
        iamForm: this
      }, {
        context: this
      });
    });
    this.validator.onSuccess(event => {
      iamotaEventHandler.trigger(["".concat(this.namespace, ":validation:success"), 'iamForm:validation:success'], {
        event,
        validator,
        iamForm: this
      }, {
        context: this
      });
    });
    this.validator.onFail(fields => {
      iamotaEventHandler.trigger(["".concat(this.namespace, ":validation:failed"), 'iamForm:validation:failed'], {
        fields,
        validator,
        iamForm: this
      }, {
        context: this
      });
    });

    // Base Validation is Ready
    iamotaEventHandler.trigger(["".concat(this.namespace, ":validation:init"), 'iamForm:validation:init'], {
      validator,
      iamForm: this
    }, {
      context: this,
      isAtomic: true
    }).then(() => {
      // Trigger a validation once everything has had a chance to register rules
      this.validateForm();
    });
  }

  /**
   * Return the validation state of the form
   */
  validateForm() {
    var _this = this;
    return _asyncToGenerator(function* () {
      if (!_this.validator) return false; // Assume invalid if the validator isn't ready yet
      return _this.validator.revalidate();
    })();
  }

  /**
   * Helper to handle form error state
   * Adds/removes the error class
   */
  setErrorState() {
    var errorMessage = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : translate('An error occurred, please try refreshing the page', "".concat(this.namespace, ".error_unknown"));
    this.classList.add('--error');
    var errorContainer = this.errorContainer;
    if (errorContainer) errorContainer.innerHTML = errorMessage;else console.error("".concat(this.namespace, ": Unable to set error state: No error container found"), errorMessage);
  }

  /**
   * Expose Loading/Disabled state methods
   */
  setLoading(isLoading) {
    this.loadable.setLoading(isLoading);
  }
  setDisabled(isDisabled) {
    this.disableable.setDisabled(isDisabled);
  }
  isLoading() {
    return this.loadable.isLoading();
  }
  isDisabled() {
    return this.disableable.isDisabled();
  }
}
/**
 * Constants
 */
_defineProperty(iamForm, "ERROR_CONTAINER_CLASS", '.form__errors-container');
export default iamForm;